import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, Info, Camera, ChevronDown, Edit3, Check, X } from 'lucide-react';
import './FrameAwareCoaching.css';

/**
 * FrameAwareCoaching Component
 *
 * Slim info bar showing: frame number, phase badge, raw measurements, pose confidence.
 * Includes manual phase correction mode for fixing incorrect air phase detection.
 */
const FrameAwareCoaching = ({
  frameNumber,
  coaching,
  confidence,
  elementPhase,
  showDetailed = true,
  onCorrectPhase,
  isRecalculating,
  isCorrected,
  elementType,
}) => {
  const [currentTheme, setCurrentTheme] = useState('light');
  const [isExpanded, setIsExpanded] = useState(false);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [airStartFrame, setAirStartFrame] = useState(null);
  const [airEndFrame, setAirEndFrame] = useState(null);

  // Detect and sync with body theme attribute
  useEffect(() => {
    const updateTheme = () => {
      const bodyTheme = document.body.getAttribute('data-theme');
      const htmlTheme = document.documentElement.getAttribute('data-theme');
      const theme = bodyTheme || htmlTheme || 'gold';
      setCurrentTheme(theme);
    };

    updateTheme();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          updateTheme();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return '#4CAF50';
    if (confidence >= 0.6) return '#FF9800';
    return '#F44336';
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'takeoff': return '🚀';
      case 'air': return '✈️';
      case 'landing': return '🎯';
      case 'entry': return '🌟';
      case 'rotation': return '🌪️';
      case 'exit': return '✨';
      default: return '📊';
    }
  };

  const getDetectionQualityInfo = (detectionQuality) => {
    if (!detectionQuality) return null;

    const statusConfig = {
      warning: {
        icon: AlertTriangle,
        color: '#F44336',
        bgColor: '#FFEBEE',
        borderColor: '#FFCDD2',
        title: 'Video Analysis Limited'
      },
      limited: {
        icon: AlertTriangle,
        color: '#FF9800',
        bgColor: '#FFF3E0',
        borderColor: '#FFE0B2',
        title: 'Limited Analysis'
      },
      moderate: {
        icon: Info,
        color: '#2196F3',
        bgColor: '#E3F2FD',
        borderColor: '#BBDEFB',
        title: 'Moderate Detection'
      },
      good: {
        icon: Camera,
        color: '#4CAF50',
        bgColor: '#E8F5E9',
        borderColor: '#C8E6C9',
        title: 'Good Detection'
      }
    };

    return statusConfig[detectionQuality.status] || statusConfig.moderate;
  };

  const shouldShowDetectionWarning = coaching && (
    coaching.can_analyze === false ||
    coaching.detection_quality?.status === 'warning' ||
    coaching.detection_quality?.status === 'limited'
  );

  if (!coaching) {
    return null;
  }

  // Only show correction for single jumps (not combos or spins)
  const isJump = elementType && elementType.startsWith('jump');
  const canCorrect = isJump && onCorrectPhase && !isRecalculating;

  const handleSetAirStart = () => {
    setAirStartFrame(frameNumber);
  };

  const handleSetAirEnd = () => {
    setAirEndFrame(frameNumber);
  };

  const handleRecalculate = () => {
    if (airStartFrame !== null && airEndFrame !== null && onCorrectPhase) {
      onCorrectPhase(airStartFrame, airEndFrame);
      setCorrectionMode(false);
    }
  };

  const handleCancelCorrection = () => {
    setCorrectionMode(false);
    setAirStartFrame(null);
    setAirEndFrame(null);
  };

  return (
    <div
      className="frame-coaching"
      data-theme={currentTheme}
      data-has-warning={shouldShowDetectionWarning || undefined}
    >
      <div className={`coaching-header ${isExpanded ? 'expanded' : ''}`} onClick={() => setIsExpanded(!isExpanded)}>
        {/* Frame number and timestamp */}
        <div className="frame-info">
          <Clock size={12} />
          <span className="frame-number">Frame {frameNumber}</span>
          {coaching?.timestamp && (
            <span className="timestamp">({coaching.timestamp.toFixed(1)}s)</span>
          )}
        </div>

        {/* Phase badge */}
        <div className="element-phase">
          <span className="phase-icon">{getPhaseIcon(elementPhase)}</span>
          <span className="phase-text">{elementPhase?.replace('_', ' ')}</span>
        </div>

        {/* Measurements */}
        {coaching?.coaching_message && (
          <div className="measurements-inline">
            {coaching.coaching_message}
          </div>
        )}

        {/* Expand indicator */}
        {coaching?.coaching_message && (
          <span className="expand-indicator">
            <ChevronDown size={12} />
          </span>
        )}

        {/* Pose Confidence */}
        {confidence !== undefined && (
          <div className="confidence-display">
            <div
              className="confidence-circle"
              style={{
                '--confidence': confidence,
                '--color': getConfidenceColor(confidence),
              }}
            >
              <span>{(confidence * 100).toFixed(0)}%</span>
            </div>
            <span className="confidence-label">Pose</span>
          </div>
        )}
      </div>

      {/* Detection Quality Warning Banner */}
      {shouldShowDetectionWarning && coaching?.detection_quality && (() => {
        const qualityInfo = getDetectionQualityInfo(coaching.detection_quality);
        const IconComponent = qualityInfo?.icon;
        return (
          <div
            className="detection-warning-banner"
            style={{
              backgroundColor: qualityInfo?.bgColor,
              borderColor: qualityInfo?.borderColor,
            }}
          >
            <div className="warning-header">
              {IconComponent && <IconComponent size={14} color={qualityInfo?.color} />}
              <span style={{ fontWeight: 600, color: qualityInfo?.color }}>
                {qualityInfo?.title}
              </span>
              <span className="warning-recommendation">
                {coaching.detection_quality.recommendation}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Phase Correction UI */}
      {canCorrect && !correctionMode && !isCorrected && (
        <button
          className="phase-correct-btn"
          onClick={() => setCorrectionMode(true)}
          title="Correct air phase boundaries"
        >
          <Edit3 size={12} /> Correct Phase
        </button>
      )}

      {isCorrected && !correctionMode && (
        <span className="phase-corrected-badge">
          <Check size={12} /> Manually corrected
        </span>
      )}

      {correctionMode && (
        <div className="phase-correction-panel">
          <div className="correction-instructions">
            Scrub video to the correct frame, then tap to mark:
          </div>
          <div className="correction-buttons">
            <button
              className={`correction-mark-btn ${airStartFrame !== null ? 'set' : ''}`}
              onClick={handleSetAirStart}
            >
              Set Air Start {airStartFrame !== null ? `(${airStartFrame})` : ''}
            </button>
            <button
              className={`correction-mark-btn ${airEndFrame !== null ? 'set' : ''}`}
              onClick={handleSetAirEnd}
            >
              Set Air End {airEndFrame !== null ? `(${airEndFrame})` : ''}
            </button>
          </div>
          {airStartFrame !== null && airEndFrame !== null && (
            <div className="correction-confirm">
              <span>Air Phase: {airStartFrame} → {airEndFrame}</span>
              <button className="correction-recalc-btn" onClick={handleRecalculate}>
                Recalculate
              </button>
            </div>
          )}
          <button className="correction-cancel-btn" onClick={handleCancelCorrection}>
            <X size={12} /> Cancel
          </button>
        </div>
      )}

      {isRecalculating && (
        <div className="phase-recalculating">
          Recalculating with corrected phases...
        </div>
      )}
    </div>
  );
};

export default FrameAwareCoaching;
